import { ArrowRight } from "lucide-react";
import { Reveal, Label } from "./Reveal";
import { Thread } from "./Thread";

// Problem-intent authority section: the real situations parents search for,
// in parent language. Each linked problem is a descriptive internal anchor
// into the matching stage authority page — the homepage's problem-intent
// branch of the internal linking structure.
const PROBLEMS = [
  { text: "A baby waking every hour or two through the night", href: "/baby-sleep-consultant" },
  { text: "Naps that end at 30 minutes — every single time", href: "/baby-sleep-consultant" },
  { text: "A newborn who has days and nights mixed up", href: "/newborn-sleep-consultant" },
  { text: "Contact naps — they only sleep on you", href: "/baby-sleep-consultant" },
  { text: "Waking the moment they're put down", href: "/baby-sleep-consultant" },
  { text: "Bedtime that takes an hour — or three", href: "/toddler-sleep-consultant" },
  { text: "A toddler who keeps coming back out of their room", href: "/toddler-sleep-consultant" },
  { text: "5 a.m. wake-ups that quietly became the norm", href: "/toddler-sleep-consultant" },
  { text: "A nap transition that threw the whole day off", href: "/baby-sleep-consultant" },
  { text: "A regression that never really ended", href: "/baby-sleep-consultant" },
  { text: "A schedule that only works if nothing ever changes", href: null },
  { text: "\u201cWe\u2019ve tried everything — and nothing sticks.\u201d", href: null },
];

export const ProblemAuthority = () => (
  <section id="sleep-problems" data-testid="problem-authority-section" className="bg-ivory">
    <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-20 pt-6 lg:pt-10">
      <Thread className="text-clay/40" flip />
    </div>

    <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-20 py-24 lg:py-36 grid grid-cols-1 lg:grid-cols-12 gap-14">
      <div className="lg:col-span-5">
        <div className="lg:sticky lg:top-28">
          <Reveal>
            <Label>What brings families here</Label>
          </Reveal>
          <Reveal as="h2" delay={0.08} className="mt-6 font-serif font-light text-ink text-4xl sm:text-5xl leading-[1.08] tracking-tight">
            What can a pediatric
            <span className="block">sleep consultant</span>
            <span className="block italic text-clay">help with?</span>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-8 max-w-md text-base sm:text-lg font-normal leading-relaxed text-smoke">
              Most families don&rsquo;t arrive with one tidy problem. They
              arrive with a pattern — weeks of it — and a quiet feeling that
              they&rsquo;re the only ones who can&rsquo;t crack it.
            </p>
            <p className="mt-5 max-w-md text-base sm:text-lg font-normal leading-relaxed text-smoke">
              You&rsquo;re not. These are the exact situations I work through
              with parents every week:
            </p>
          </Reveal>
        </div>
      </div>

      <div className="lg:col-span-6 lg:col-start-7">
        <Reveal delay={0.1}>
          <ul className="border-t border-ink/10">
            {PROBLEMS.map((p, i) => (
              <li key={p.text} data-testid={`problem-${i}`} className="border-b border-ink/10">
                {p.href ? (
                  <a
                    href={p.href}
                    className="group flex items-baseline justify-between gap-6 py-5"
                  >
                    <span className="text-base sm:text-lg font-normal leading-relaxed text-smoke group-hover:text-ink transition-colors duration-300">
                      {p.text}
                    </span>
                    <ArrowRight size={15} aria-hidden="true" className="shrink-0 text-clay/60 transition-transform duration-300 group-hover:translate-x-1 group-hover:text-clay" />
                  </a>
                ) : (
                  <p className="py-5 text-base sm:text-lg font-normal leading-relaxed text-smoke">
                    {p.text}
                  </p>
                )}
              </li>
            ))}
          </ul>
        </Reveal>
        <Reveal delay={0.18}>
          <p data-testid="problem-closing" className="mt-10 font-serif italic font-light text-xl sm:text-2xl leading-snug text-ink/70 max-w-lg">
            If you recognized your nights in that list — that&rsquo;s not a
            failure.
            <span className="block mt-1 text-clay">That&rsquo;s a starting point.</span>
          </p>
        </Reveal>
      </div>
    </div>
  </section>
);
